import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";

function newSessionId() {
  let sid = sessionStorage.getItem("zatayoo_chat_sid");
  if (!sid) { sid = `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`; sessionStorage.setItem("zatayoo_chat_sid", sid); }
  return sid;
}

const SUGGESTIONS = ["Best time for Spiti Valley?", "Hidden beaches near Goa", "3-day trek for beginners"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste! I'm your Zatayoo travel guide 🏔️ Ask me about hidden places, treks, best seasons, budgets — anything India." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sid = useRef(newSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sid.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the guide just now. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-50 grid place-items-center w-14 h-14 rounded-full bg-accent text-accent-foreground shadow-lg hover:-translate-y-0.5 transition-transform">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm rounded-2xl border border-primary/10 bg-card shadow-2xl flex flex-col overflow-hidden" style={{ height: "min(560px, 75vh)" }} data-testid="chat-panel">
          <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-accent"><Sparkles size={16} /></span>
            <div><p className="font-semibold leading-tight">Zatayoo Guide</p><p className="text-xs text-primary-foreground/70">Powered by Gemini</p></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-accent text-accent-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-secondary rounded-2xl px-4 py-2.5"><Loader2 size={16} className="animate-spin text-muted-foreground" /></div></div>}
            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="text-xs rounded-full border border-primary/20 px-3 py-1.5 text-foreground/70 hover:bg-secondary" data-testid={`chat-suggest`}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-primary/10 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your trip…" data-testid="chat-input"
              className="flex-1 h-11 rounded-full border border-input bg-transparent px-4 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send"
              className="grid place-items-center w-11 h-11 rounded-full bg-accent text-accent-foreground disabled:opacity-50 shrink-0"><Send size={18} /></button>
          </form>
        </div>
      )}
    </>
  );
}
